import {
  AtsCandidatePullMode,
  AtsResultPushConfigInput,
  AtsResultType,
  AtsSettingsQuery,
  AtsStageMapEntryInput,
  AtsStageRejectionReasonsEntryInput,
  SzMappedStageType,
} from 'generated/graphql';

export type AtsSettings = NonNullable<AtsSettingsQuery['atsSettings']>;
export type ResultPushEntry = AtsSettings['resultPush'][number];
export type StageMapEntry = AtsSettings['stageMap'][number];
export type StageReasonEntry = AtsSettings['stageRejectionReasons'][number];

/** Reason ids per ATS stage ref, as edited in the stage rejection reasons card. */
export type StageReasonSelection = Record<string, string[]>;

/** One ATS stage from the catalogue, shaped for the pickers. */
export type StageOption = { label: string; value: string };

export const ICIMS_TOOL = 'icims';
export const WORKDAY_TOOL = 'workday';
export const WORKABLE_TOOL = 'workable';

/**
 * Row order mirrors the candidate journey: shortlist → interview →
 * assessment → human interview evaluation.
 */
export const RESULT_TYPES = [
  AtsResultType.AiShortlist,
  AtsResultType.AiInterview,
  AtsResultType.Assessment,
  AtsResultType.InterviewEvaluation,
] as const;

/** The gateway rejects `sendAssessmentOrder` for types without orders. */
export function hasAssessmentOrder(resultType: AtsResultType): boolean {
  return (
    resultType !== AtsResultType.AiShortlist &&
    resultType !== AtsResultType.InterviewEvaluation
  );
}

/**
 * Types without an assessment order have no completion either, so the
 * gateway rejects a completion stage for them.
 */
export function hasCompletionStage(resultType: AtsResultType): boolean {
  return hasAssessmentOrder(resultType);
}

export const RESULT_TYPE_LABELS: Record<AtsResultType, string> = {
  [AtsResultType.AiShortlist]: 'AI Shortlist results',
  [AtsResultType.AiInterview]: 'AI Interview results',
  [AtsResultType.Assessment]: 'Assessment results',
  [AtsResultType.InterviewEvaluation]: 'Interview evaluation results',
};

export type FieldKey =
  | 'score'
  | 'resultUrl'
  | 'completionDate'
  | 'orderStatus'
  | 'integrityRisk';

/** Canonical value keys the gateway accepts per result type (its whitelist). */
export const FIELD_KEYS_BY_TYPE: Record<AtsResultType, readonly FieldKey[]> = {
  [AtsResultType.AiShortlist]: ['score', 'resultUrl'],
  [AtsResultType.AiInterview]: [
    'score',
    'resultUrl',
    'completionDate',
    'orderStatus',
    'integrityRisk',
  ],
  [AtsResultType.Assessment]: [
    'score',
    'resultUrl',
    'completionDate',
    'orderStatus',
  ],
  [AtsResultType.InterviewEvaluation]: [],
};

/** Keys writable to a Workday custom object. */
export type WorkdayFieldKey =
  | Exclude<FieldKey, 'integrityRisk'>
  | 'interviewName'
  | 'evaluatorName'
  | 'recommendedDecision'
  | 'evaluationId';

/**
 * Narrower than {@link FIELD_KEYS_BY_TYPE}: risk maps to a Workday Custom
 * List field, which needs list value aliases — a third config level that is
 * out of scope. The gateway enforces the same whitelist.
 */
export const WORKDAY_FIELD_KEYS_BY_TYPE: Record<
  AtsResultType,
  readonly WorkdayFieldKey[]
> = {
  [AtsResultType.AiShortlist]: ['score', 'resultUrl'],
  [AtsResultType.AiInterview]: [
    'score',
    'resultUrl',
    'completionDate',
    'orderStatus',
  ],
  [AtsResultType.Assessment]: [
    'score',
    'resultUrl',
    'completionDate',
    'orderStatus',
  ],
  [AtsResultType.InterviewEvaluation]: [
    'evaluationId',
    'interviewName',
    'evaluatorName',
    'recommendedDecision',
    'completionDate',
    'resultUrl',
  ],
};

/**
 * The status is written as the plain string Invited / Completed / Error, so
 * a Custom List field on the tenant would reject it.
 */
export const WORKDAY_FIELD_HINTS: Partial<Record<WorkdayFieldKey, string>> = {
  orderStatus: 'Must be a Text field on the custom object.',
  evaluationId: 'Text field set as the object’s Reference ID.',
};

export const FIELD_LABELS: Record<FieldKey | WorkdayFieldKey, string> = {
  score: 'Score',
  resultUrl: 'Result URL',
  completionDate: 'Completion date',
  orderStatus: 'Order status',
  integrityRisk: 'Integrity risk level',
  interviewName: 'Interview name',
  evaluatorName: 'Evaluator name',
  recommendedDecision: 'Recommended decision',
  evaluationId: 'Evaluation ID',
};

const FIELD_LABEL_OVERRIDES: Partial<
  Record<AtsResultType, Partial<Record<FieldKey | WorkdayFieldKey, string>>>
> = {
  [AtsResultType.InterviewEvaluation]: { completionDate: 'Submission date' },
};

export function fieldLabel(
  resultType: AtsResultType,
  fieldKey: FieldKey | WorkdayFieldKey
): string {
  return (
    FIELD_LABEL_OVERRIDES[resultType]?.[fieldKey] ?? FIELD_LABELS[fieldKey]
  );
}

/**
 * integrityRisk is accepted by the gateway whitelist but no push path
 * supplies its value yet — an entered ID is stored, never written. Flag it
 * so admins don't configure a dead field.
 */
export const FIELD_HINTS: Partial<Record<FieldKey, string>> = {
  integrityRisk: 'Not written by Spotted Zebra yet.',
};

export type ResultPushFormValue = {
  sendAssessmentOrder: boolean;
  sendResultLink: boolean;
  sendToAtsFields: boolean;
  fieldIds: Partial<Record<FieldKey, string>>;
  sendToWorkdayObjects: boolean;
  objectAlias: string;
  fieldAliases: Partial<Record<WorkdayFieldKey, string>>;
  /** Empty means no completion move for this result type. */
  completionStageRef: string;
};

export type ResultPushFormValues = Record<AtsResultType, ResultPushFormValue>;

export function toFormResultPush(
  atsSettings: AtsSettings | null
): ResultPushFormValues {
  const byType = new Map<AtsResultType, ResultPushEntry>(
    (atsSettings?.resultPush ?? []).map(entry => [entry.resultType, entry])
  );
  const build = (resultType: AtsResultType): ResultPushFormValue => {
    const entry = byType.get(resultType);
    return {
      sendAssessmentOrder: entry?.sendAssessmentOrder ?? true,
      sendResultLink: entry?.sendResultLink ?? false,
      sendToAtsFields: entry?.sendToAtsFields ?? false,
      fieldIds: Object.fromEntries(
        FIELD_KEYS_BY_TYPE[resultType].map(key => [
          key,
          entry?.fieldIds?.[key] ?? '',
        ])
      ),
      sendToWorkdayObjects: entry?.sendToWorkdayObjects ?? false,
      objectAlias: entry?.objectAlias ?? '',
      fieldAliases: Object.fromEntries(
        WORKDAY_FIELD_KEYS_BY_TYPE[resultType].map(key => [
          key,
          entry?.fieldAliases?.[key] ?? '',
        ])
      ),
      completionStageRef: entry?.completionStageRef ?? '',
    };
  };
  return {
    [AtsResultType.AiShortlist]: build(AtsResultType.AiShortlist),
    [AtsResultType.AiInterview]: build(AtsResultType.AiInterview),
    [AtsResultType.Assessment]: build(AtsResultType.Assessment),
    [AtsResultType.InterviewEvaluation]: build(
      AtsResultType.InterviewEvaluation
    ),
  };
}

/**
 * Build the mutation payload: full state per result type (the gateway
 * replaces fieldIds / fieldAliases wholesale, so blanks clear a mapping).
 * Types without orders omit sendAssessmentOrder — the gateway rejects it —
 * and each tool's passthrough keys travel only for that tool (and only for
 * types that have fields in it), which the gateway also enforces.
 */
export function toResultPushConfigs(
  values: ResultPushFormValues,
  isIcims: boolean,
  isWorkday = false
): AtsResultPushConfigInput[] {
  return RESULT_TYPES.map(resultType => {
    const value = values[resultType];
    return {
      resultType,
      ...(hasAssessmentOrder(resultType) && {
        sendAssessmentOrder: value.sendAssessmentOrder,
      }),
      sendResultLink: value.sendResultLink,
      ...(isIcims &&
        FIELD_KEYS_BY_TYPE[resultType].length > 0 && {
          sendToAtsFields: value.sendToAtsFields,
          fieldIds: Object.fromEntries(
            FIELD_KEYS_BY_TYPE[resultType].map(key => [
              key,
              value.fieldIds[key]?.trim() || null,
            ])
          ),
        }),
      ...(isWorkday && {
        sendToWorkdayObjects: value.sendToWorkdayObjects,
        objectAlias: value.objectAlias.trim(),
        fieldAliases: Object.fromEntries(
          WORKDAY_FIELD_KEYS_BY_TYPE[resultType].map(key => [
            key,
            value.fieldAliases[key]?.trim() || null,
          ])
        ),
      }),
      // Null clears a stored target; the gateway rejects the key for other
      // tools.
      ...(isIcims &&
        hasCompletionStage(resultType) && {
          completionStageRef: value.completionStageRef.trim() || null,
        }),
    };
  });
}

export function onOff(value: boolean): string {
  return value ? 'On' : 'Off';
}

export function resultPushSummary(
  resultType: AtsResultType,
  entry: ResultPushEntry | undefined,
  isIcims: boolean,
  isWorkday = false,
  stageNamesByRef?: Map<string, string>
): string {
  const parts: string[] = [];
  if (hasAssessmentOrder(resultType)) {
    parts.push(`Order result: ${onOff(entry?.sendAssessmentOrder ?? true)}`);
  }
  parts.push(`Result link: ${onOff(entry?.sendResultLink ?? false)}`);
  if (isIcims && FIELD_KEYS_BY_TYPE[resultType].length > 0) {
    const configured = entry
      ? FIELD_KEYS_BY_TYPE[resultType].filter(key => entry.fieldIds?.[key])
          .length
      : 0;
    parts.push(
      `iCIMS fields: ${onOff(entry?.sendToAtsFields ?? false)}${
        configured > 0 ? ` (${configured} configured)` : ''
      }`
    );
  }
  if (isWorkday) {
    const configured = entry
      ? WORKDAY_FIELD_KEYS_BY_TYPE[resultType].filter(
          key => entry.fieldAliases?.[key]
        ).length
      : 0;
    // The object alias is shown because without it nothing is written, no
    // matter how many field aliases are set.
    const objectAlias = entry?.objectAlias
      ? ` (${entry.objectAlias}, ${configured} field${
          configured === 1 ? '' : 's'
        })`
      : '';
    parts.push(
      `Workday objects: ${onOff(
        entry?.sendToWorkdayObjects ?? false
      )}${objectAlias}`
    );
  }
  if (isIcims && hasCompletionStage(resultType)) {
    const ref = entry?.completionStageRef;
    const name = ref ? stageNamesByRef?.get(ref) : undefined;
    parts.push(
      `Move on completion: ${ref ? (name ? `${name} (${ref})` : ref) : 'Off'}`
    );
  }
  return parts.join(' · ');
}

/** Null/empty means no filter — recruiters get the whole ATS catalogue. */
export function allowedReasonsSummary(
  ids: readonly string[] | null | undefined
): string {
  if (!ids || ids.length === 0) {
    return 'All reasons allowed';
  }
  return `${ids.length} reason${ids.length === 1 ? '' : 's'} allowed`;
}

/** Row order mirrors the candidate journey. */
export const SZ_STAGE_TYPES = [
  SzMappedStageType.AiShortlist,
  SzMappedStageType.AiInterview,
  SzMappedStageType.Assessment,
  SzMappedStageType.Interview,
  SzMappedStageType.Offer,
] as const;

export const SZ_STAGE_TYPE_LABELS: Record<SzMappedStageType, string> = {
  [SzMappedStageType.AiShortlist]: 'AI Shortlist',
  [SzMappedStageType.AiInterview]: 'AI Interview',
  [SzMappedStageType.Assessment]: 'Assessment',
  [SzMappedStageType.Interview]: 'Interview',
  [SzMappedStageType.Offer]: 'Offer',
};

export const PULL_MODE_LABELS: Record<AtsCandidatePullMode, string> = {
  [AtsCandidatePullMode.All]: 'All applicants on a linked role',
  [AtsCandidatePullMode.MappedStages]: 'Only on entering a mapped stage',
};

export type StageMapFormValue = {
  atsStageRef: string;
  /** Raw text-area value: Workday step ids, one per line. */
  workdayStepIds: string;
};

export type StageMapFormValues = Record<SzMappedStageType, StageMapFormValue>;

/** Clashing step ids named in the duplicate message before "and N more". */
const MAX_LISTED_STEP_CLASHES = 10;

/**
 * Step ids typed or pasted into the text area: split on newlines, commas
 * and whitespace, blanks dropped, repeats removed in first-seen order.
 */
export function parseStepIds(raw: string): string[] {
  return [...new Set(raw.split(/[\s,]+/).filter(id => id !== ''))];
}

export function toFormStageMap(
  atsSettings: AtsSettings | null
): StageMapFormValues {
  const byType = new Map<SzMappedStageType, StageMapEntry>(
    (atsSettings?.stageMap ?? []).map(entry => [entry.szStageType, entry])
  );
  const build = (szStageType: SzMappedStageType): StageMapFormValue => ({
    atsStageRef: byType.get(szStageType)?.atsStageRef ?? '',
    workdayStepIds: (byType.get(szStageType)?.workdayStepIds ?? []).join('\n'),
  });
  return {
    [SzMappedStageType.AiShortlist]: build(SzMappedStageType.AiShortlist),
    [SzMappedStageType.AiInterview]: build(SzMappedStageType.AiInterview),
    [SzMappedStageType.Assessment]: build(SzMappedStageType.Assessment),
    [SzMappedStageType.Interview]: build(SzMappedStageType.Interview),
    [SzMappedStageType.Offer]: build(SzMappedStageType.Offer),
  };
}

/**
 * Build the mutation payload: the map is replaced wholesale, so an unset
 * stage type simply drops out. Step ids only travel for Workday — the
 * gateway rejects them for every other tool.
 */
export function toStageMapEntries(
  values: StageMapFormValues,
  isWorkday: boolean
): AtsStageMapEntryInput[] {
  return SZ_STAGE_TYPES.filter(
    szStageType => values[szStageType].atsStageRef.trim() !== ''
  ).map(szStageType => {
    const value = values[szStageType];
    const workdayStepIds = parseStepIds(value.workdayStepIds);
    return {
      szStageType,
      atsStageRef: value.atsStageRef.trim(),
      ...(isWorkday && workdayStepIds.length > 0 ? { workdayStepIds } : {}),
    };
  });
}

/**
 * Mirrors the gateway's stage-map rules so a clash is caught before the
 * round-trip, naming the offenders. On Workday a stage type is told apart
 * by its business-process steps, because Workday nests several SZ stages
 * under one parent stage: each step id may belong to one stage type only
 * (all clashes are listed, for large pastes), and a parent stage may back
 * at most one stage type without steps. Elsewhere there are no steps, so
 * an ATS stage backs at most one stage type.
 */
export function findDuplicateStageRef(
  values: StageMapFormValues,
  isWorkday = false
): string | null {
  const stepLessByRef = new Map<string, SzMappedStageType>();
  const ownersByStep = new Map<string, SzMappedStageType[]>();
  for (const szStageType of SZ_STAGE_TYPES) {
    const value = values[szStageType];
    const ref = value.atsStageRef.trim();
    if (ref === '') continue;
    const stepIds = isWorkday ? parseStepIds(value.workdayStepIds) : [];
    for (const stepId of stepIds) {
      ownersByStep.set(stepId, [
        ...(ownersByStep.get(stepId) ?? []),
        szStageType,
      ]);
    }
    if (stepIds.length > 0) continue;
    const existing = stepLessByRef.get(ref);
    if (existing) {
      return isWorkday
        ? `${SZ_STAGE_TYPE_LABELS[existing]} and ${SZ_STAGE_TYPE_LABELS[szStageType]} are mapped to the same ATS stage without steps — give them distinct Workday step IDs.`
        : `${SZ_STAGE_TYPE_LABELS[existing]} and ${SZ_STAGE_TYPE_LABELS[szStageType]} are mapped to the same ATS stage.`;
    }
    stepLessByRef.set(ref, szStageType);
  }

  const clashes = [...ownersByStep]
    .filter(([, owners]) => owners.length > 1)
    .map(
      ([stepId, owners]) =>
        `${stepId} (${owners.map(type => SZ_STAGE_TYPE_LABELS[type]).join(', ')})`
    );
  if (clashes.length === 0) return null;
  const listed = clashes.slice(0, MAX_LISTED_STEP_CLASHES).join('; ');
  const more =
    clashes.length > MAX_LISTED_STEP_CLASHES
      ? ` and ${clashes.length - MAX_LISTED_STEP_CLASHES} more`
      : '';
  return `Each Workday step ID can belong to one stage type only. Mapped more than once: ${listed}${more}.`;
}

/** The gateway enforces this too; here the message can name both sides. */
export function findCompletionStageClash(
  values: ResultPushFormValues,
  stageMap: readonly StageMapEntry[] | undefined
): string | null {
  const mapped = new Map<string, SzMappedStageType>();
  for (const entry of stageMap ?? []) {
    const ref = entry.atsStageRef.trim();
    if (ref && !mapped.has(ref)) mapped.set(ref, entry.szStageType);
  }
  for (const resultType of RESULT_TYPES) {
    if (!hasCompletionStage(resultType)) continue;
    const ref = values[resultType].completionStageRef.trim();
    if (!ref) continue;
    const clash = mapped.get(ref);
    if (clash) {
      return `${RESULT_TYPE_LABELS[resultType]} would move completed candidates into the stage mapped to ${SZ_STAGE_TYPE_LABELS[clash]} — pick a different stage.`;
    }
  }
  return null;
}

/** The same rule, checked from the stage map's side. */
export function findStageMapCompletionClash(
  values: StageMapFormValues,
  resultPush: readonly ResultPushEntry[] | undefined
): string | null {
  const targets = new Map<string, AtsResultType>();
  for (const entry of resultPush ?? []) {
    const ref = entry.completionStageRef?.trim();
    if (ref && !targets.has(ref)) targets.set(ref, entry.resultType);
  }
  for (const szStageType of SZ_STAGE_TYPES) {
    const ref = values[szStageType].atsStageRef.trim();
    if (!ref) continue;
    const clash = targets.get(ref);
    if (clash) {
      return `${SZ_STAGE_TYPE_LABELS[szStageType]} is mapped to the stage ${RESULT_TYPE_LABELS[clash]} moves completed candidates into — pick a different stage.`;
    }
  }
  return null;
}

/**
 * Read-mode summary for one SZ stage type. A ref with no catalogue match
 * is a stage removed (or not yet synced) since it was saved — show the raw
 * ref rather than dropping it, so it stays visible and fixable.
 */
export function stageMapSummary(
  entry: StageMapEntry | undefined,
  stageNamesByRef: Map<string, string>
): string {
  if (!entry) return 'Not mapped';
  const name = stageNamesByRef.get(entry.atsStageRef);
  const label = name ? `${name} (${entry.atsStageRef})` : entry.atsStageRef;
  const steps = entry.workdayStepIds;
  if (steps.length === 0) return label;
  return steps.length === 1
    ? `${label} · step ${steps[0]}`
    : `${label} · ${steps.length} steps`;
}

export function toStageReasonSelection(
  config: readonly StageReasonEntry[]
): StageReasonSelection {
  return Object.fromEntries(
    config.map(entry => [entry.atsStageRef, [...entry.rejectionReasonIds]])
  );
}

/**
 * The entries to save: stages with at least one reason, keeping any saved
 * default auto-reject reason. A stage with none is left unconfigured
 * rather than saved as "no valid reason"; `null` clears the configuration.
 */
export function buildStageReasonEntries(
  selection: StageReasonSelection,
  saved: readonly StageReasonEntry[]
): AtsStageRejectionReasonsEntryInput[] | null {
  const savedDefaults = new Map(
    saved.map(entry => [entry.atsStageRef, entry.defaultAutoRejectReasonId])
  );
  const entries = Object.entries(selection)
    .filter(([, reasonIds]) => reasonIds.length > 0)
    .map(([atsStageRef, rejectionReasonIds]) => {
      const defaultAutoRejectReasonId = savedDefaults.get(atsStageRef);
      return {
        atsStageRef,
        rejectionReasonIds,
        ...(defaultAutoRejectReasonId ? { defaultAutoRejectReasonId } : {}),
      };
    });
  return entries.length > 0 ? entries : null;
}

/** Set semantics per stage: reordering is not a change, nor is emptying an unconfigured stage. */
export function stageReasonsChanged(
  selection: StageReasonSelection,
  saved: readonly StageReasonEntry[]
): boolean {
  const normalise = (byStage: StageReasonSelection) =>
    JSON.stringify(
      Object.entries(byStage)
        .filter(([, reasonIds]) => reasonIds.length > 0)
        .map(([stageRef, reasonIds]) => [stageRef, [...reasonIds].sort()])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))
    );
  return normalise(selection) !== normalise(toStageReasonSelection(saved));
}

export type StageReasonIssues = {
  /** Saved reasons no longer in Workday's reason list. */
  missingReasonIds: string[];
  /** Saved reasons the global allow-list hides from recruiters. */
  excludedReasonIds: string[];
  /** Recruiters cannot reject from this stage: no saved reason is usable. */
  hasNoUsableReason: boolean;
  /** The stage itself is no longer in Workday's stage list. */
  isStageMissing: boolean;
};

/**
 * Configuration drift per configured stage; stages without issues are
 * omitted. An empty stage or reason list means it failed to load, so
 * nothing is flagged against it.
 */
export function findStageReasonIssues(
  selection: StageReasonSelection,
  lists: {
    stageRefs: readonly string[];
    reasonIds: readonly string[];
    allowedReasonIds: readonly string[] | null | undefined;
  }
): Record<string, StageReasonIssues> {
  const knownStages = new Set(lists.stageRefs);
  const knownReasons = new Set(lists.reasonIds);
  const allowed =
    lists.allowedReasonIds && lists.allowedReasonIds.length > 0
      ? new Set(lists.allowedReasonIds)
      : null;
  const reasonsLoaded = knownReasons.size > 0;

  const issues: Record<string, StageReasonIssues> = {};
  for (const [stageRef, reasonIds] of Object.entries(selection)) {
    if (reasonIds.length === 0) continue;
    const missingReasonIds = reasonsLoaded
      ? reasonIds.filter(id => !knownReasons.has(id))
      : [];
    const excludedReasonIds = allowed
      ? reasonIds.filter(
          id => !allowed.has(id) && !missingReasonIds.includes(id)
        )
      : [];
    const stageIssues = {
      missingReasonIds,
      excludedReasonIds,
      hasNoUsableReason:
        reasonsLoaded &&
        missingReasonIds.length + excludedReasonIds.length === reasonIds.length,
      isStageMissing: knownStages.size > 0 && !knownStages.has(stageRef),
    };
    if (
      stageIssues.missingReasonIds.length > 0 ||
      stageIssues.excludedReasonIds.length > 0 ||
      stageIssues.hasNoUsableReason ||
      stageIssues.isStageMissing
    ) {
      issues[stageRef] = stageIssues;
    }
  }
  return issues;
}
